<template>
  <div>
    <DataTable :value="rows" :loading="loading" class="p-datatable-sm" dataKey="projectTypeId">
      <Column field="name" header="Quote type" />
      <Column field="emails" header="Emails" />
      <Column field="steps" header="Steps" />
      <Column field="noReply" header="No reply after" />
      <Column field="told" header="Who is told at no reply" />
      <Column field="chaseCall" header="Old chase call on booking" />
      <Column header="" :styles="{ width: '6rem' }">
        <template #body="slot">
          <Button :id="'editType' + slot.data.projectTypeId" label="Edit" icon="pi pi-pencil" class="p-button-text p-button-sm"
            @click="edit(slot.data.projectTypeId)" />
        </template>
      </Column>
    </DataTable>

    <Dialog :visible.sync="dialog" :header="editing ? 'Follow-ups for ' + editing.name + ' quotes' : ''" :modal="true"
      :style="{ width: '760px' }" :contentStyle="{ maxHeight: '70vh', overflowY: 'auto' }">
      <div v-if="editing" class="p-fluid">
        <div class="p-field p-d-flex p-ai-center">
          <InputSwitch id="typeEmailsOn" v-model="form.emailsOn" />
          <label for="typeEmailsOn" class="p-ml-2 p-mb-0">Send follow-up emails for {{ editing.name }} quotes</label>
        </div>

        <div class="p-field">
          <label>Steps</label>
          <div class="p-field-radiobutton">
            <RadioButton id="typeStepsDefault" name="typeSteps" :value="false" v-model="form.ownSteps" />
            <label for="typeStepsDefault">Use the default steps ({{ defaultDaysText }})</label>
          </div>
          <div class="p-field-radiobutton">
            <RadioButton id="typeStepsOwn" name="typeSteps" :value="true" v-model="form.ownSteps" />
            <label for="typeStepsOwn">Its own steps and days</label>
          </div>
          <small class="hint">Choosing its own steps starts from a copy of the default steps, which you can then change.</small>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <div class="p-field-checkbox">
              <Checkbox id="typeOwnSilence" v-model="form.ownSilence" :binary="true" />
              <label for="typeOwnSilence">Its own days of silence before "no reply"</label>
            </div>
            <InputNumber id="typeSilenceDays" v-model="form.silenceEscalationDays" :min="1" :useGrouping="false"
              :disabled="!form.ownSilence" :class="{ 'p-invalid': errors.silenceEscalationDays }" />
            <small class="hint" v-if="!form.ownSilence">Default: {{ defaults.silence }} days.</small>
            <small class="p-invalid" v-if="errors.silenceEscalationDays">{{ errors.silenceEscalationDays }}</small>
          </div>
          <div class="p-field p-col">
            <label for="typeRecipient">Who is told at no reply</label>
            <Dropdown id="typeRecipient" v-model="form.escalationRecipient" :options="recipientOptions"
              optionLabel="label" optionValue="value" :class="{ 'p-invalid': errors.escalationRecipient }" />
            <small class="hint">The salesperson always gets a chase task; the customer gets the escalation email.</small>
            <small class="p-invalid" v-if="errors.escalationRecipient">{{ errors.escalationRecipient }}</small>
          </div>
        </div>

        <div class="p-field">
          <label for="typeChaseCall">Create the old automatic chase call when a quote is booked</label>
          <Dropdown id="typeChaseCall" v-model="form.chaseCallOnBooking" :options="chaseCallOptions"
            optionLabel="label" optionValue="value" :class="{ 'p-invalid': errors.chaseCallOnBooking }" />
          <small class="hint">The Call "Automated: Follow up on Quotation ..." a week after booking. Recommended: only while this type gets no follow-up emails, so a quote is never chased twice; with emails on, the salesperson is reminded only when nothing is logged after an email.</small>
          <small class="p-invalid" v-if="errors.chaseCallOnBooking">{{ errors.chaseCallOnBooking }}</small>
        </div>

        <div v-if="editing.ownSteps && form.ownSteps" class="p-mt-3">
          <h5>{{ editing.name }} steps</h5>
          <FollowupSteps :embedded="true" :projectTypeId="editing.projectTypeId" @changed="load" />
        </div>
        <small v-else-if="form.ownSteps" class="hint">Save to create this type's own steps; they then show here.</small>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="dialog = false" />
        <Button id="saveType" label="Save" icon="pi pi-check" class="p-button-text" :disabled="saving" @click="save" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import FollowupService, { errorMessage, fieldErrors } from '@/service/FollowupService'
import FollowupSteps from '../followupSteps/followupSteps.vue'
import { typeRow, toTypeForm, toTypePayload, whoLabel, stepDaysText, DEFAULT, CHASE_CALL_OPTIONS } from './followupTypesForm'

export default {
  // batch 3: the follow-up settings per quote type (Follow-up Settings > Quote types)
  components: { FollowupSteps },
  props: {
    // the saved default settings, for the "(default)" labels
    defaultSettings: { type: Object, default: null },
    defaultStepDays: { type: Array, default: () => [] }
  },
  data: () => ({
    followupService: new FollowupService(),
    types: [],
    loading: false,
    dialog: false,
    editingId: null,
    form: toTypeForm(null),
    chaseCallOptions: CHASE_CALL_OPTIONS,
    errors: {},
    saving: false
  }),
  computed: {
    rows() {
      return this.types.map(typeRow)
    },
    editing() {
      return this.types.find(t => t.projectTypeId === this.editingId) || null
    },
    defaults() {
      var s = this.defaultSettings || {}
      return { silence: s.silenceEscalationDays, recipient: s.escalationRecipient }
    },
    defaultDaysText() {
      return stepDaysText(this.defaultStepDays)
    },
    recipientOptions() {
      return [
        { value: DEFAULT, label: 'Default (' + whoLabel(this.defaults.recipient) + ')' },
        { value: 'SALESPERSON', label: whoLabel('SALESPERSON') },
        { value: 'CUSTOMER', label: whoLabel('CUSTOMER') },
        { value: 'BOTH', label: whoLabel('BOTH') }
      ]
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      return this.followupService.getTypes()
        .then(data => { this.types = data || [] })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The quote types could not be loaded.'), life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    edit(id) {
      this.editingId = id
      this.form = toTypeForm(this.editing)
      this.errors = {}
      this.dialog = true
    },
    save() {
      var t = this.editing
      if (!t) return
      this.saving = true
      return this.followupService.saveType(t.projectTypeId, toTypePayload(this.form))
        .then(data => {
          var i = this.types.findIndex(x => x.projectTypeId === t.projectTypeId)
          if (i > -1 && data) this.types.splice(i, 1, data)
          this.form = toTypeForm(data || t)
          this.errors = {}
          this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'The ' + t.name + ' follow-up settings were saved.', life: 5000 })
          // keep the dialog open when own steps were just created, so they can be edited at once
          if (!(data && data.ownSteps)) this.dialog = false
        })
        .catch(err => {
          this.errors = fieldErrors(err)
          this.$toast.add({ severity: 'error', summary: 'Not saved', detail: errorMessage(err, 'The settings could not be saved.'), life: 5000 })
        })
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.hint { color: #6c757d; }
</style>
